/**
 * 后端接口客户端。
 * - 统一携带 Bearer token；401 时清空登录态（由 store 订阅处理）。
 * - 统一错误结构 { error, message }，抛出 ApiError 供界面展示。
 * - AI 对话走 SSE 流式解析，不用 EventSource（EventSource 无法带 Authorization 头）。
 */
import type { AiCitation } from '@hew3/shared';

const TOKEN_KEY = 'hew3.token';

export function getToken(): string | null {
  try {
    return window.localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string | null): void {
  try {
    if (token) window.localStorage.setItem(TOKEN_KEY, token);
    else window.localStorage.removeItem(TOKEN_KEY);
  } catch {
    // 隐私模式下 localStorage 可能不可用，忽略即可（会话仍在内存中）
  }
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

let onUnauthorized: (() => void) | null = null;

/** 由 App 注册：token 失效时自动回到登录页。 */
export function setUnauthorizedHandler(handler: () => void): void {
  onUnauthorized = handler;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) headers.authorization = `Bearer ${token}`;
  if (body !== undefined) headers['content-type'] = 'application/json';

  const response = await fetch(path, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  const text = await response.text();
  const payload: unknown = text ? JSON.parse(text) : null;

  if (!response.ok) {
    const err = (payload ?? {}) as { error?: string; message?: string; details?: unknown };
    if (response.status === 401) {
      setToken(null);
      onUnauthorized?.();
    }
    throw new ApiError(response.status, err.error ?? 'request_failed', err.message ?? `请求失败（${response.status}）`, err.details);
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
};

/* ------------------------------------------------------------------ *
 * AI 对话（SSE 流式）
 * ------------------------------------------------------------------ */

export interface ChatMeta {
  sessionId: string;
  messageId: string;
  userMessageId: string;
  mode: 'llm' | 'rules';
  model: string | null;
  dataScopes: string[];
  citations: AiCitation[];
  actions: { label: string; kind: string; payload?: Record<string, string> }[];
  deniedScopes: string[];
  deniedNotice: string;
}

export interface ChatHandlers {
  onMeta?: (meta: ChatMeta) => void;
  onDelta?: (text: string) => void;
  onDone?: (payload: { messageId: string; content: string }) => void;
  onError?: (message: string) => void;
}

interface SseEvent {
  event: string;
  data: string;
}

function parseSseBuffer(buffer: string): { events: SseEvent[]; rest: string } {
  const events: SseEvent[] = [];
  const blocks = buffer.split('\n\n');
  const rest = blocks.pop() ?? '';
  for (const block of blocks) {
    const trimmed = block.trim();
    if (!trimmed) continue;
    let event = 'message';
    let data = '';
    for (const line of trimmed.split('\n')) {
      if (line.startsWith('event:')) event = line.slice(6).trim();
      else if (line.startsWith('data:')) data += line.slice(5).trim();
    }
    if (data) events.push({ event, data });
  }
  return { events, rest };
}

/** 发起一次流式提问。返回的 Promise 在流结束（或被中止）后 resolve。 */
export async function streamChat(
  message: string,
  sessionId: string | undefined,
  handlers: ChatHandlers,
  signal?: AbortSignal,
): Promise<void> {
  const headers: Record<string, string> = { 'content-type': 'application/json' };
  const token = getToken();
  if (token) headers.authorization = `Bearer ${token}`;

  const response = await fetch('/api/ai/chat', {
    method: 'POST',
    headers,
    body: JSON.stringify({ message, sessionId }),
    signal,
  });

  if (!response.ok || !response.body) {
    const text = await response.text().catch(() => '');
    let message2 = `AI 服务返回 ${response.status}`;
    try {
      const parsed = JSON.parse(text) as { message?: string };
      if (parsed.message) message2 = parsed.message;
    } catch {
      // 保留默认文案
    }
    if (response.status === 401) {
      setToken(null);
      onUnauthorized?.();
    }
    throw new ApiError(response.status, 'ai_failed', message2);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const { events, rest } = parseSseBuffer(buffer);
    buffer = rest;
    for (const ev of events) {
      let payload: unknown = null;
      try {
        payload = JSON.parse(ev.data);
      } catch {
        continue;
      }
      if (ev.event === 'meta') handlers.onMeta?.(payload as ChatMeta);
      else if (ev.event === 'delta') handlers.onDelta?.((payload as { text: string }).text);
      else if (ev.event === 'done') handlers.onDone?.(payload as { messageId: string; content: string });
      else if (ev.event === 'error') handlers.onError?.((payload as { message: string }).message);
    }
  }
}
